/**
 * Provider setup template
 */

export function getProviderTemplate(): string {
  return `/**
 * Vibeship DevTools Provider Setup
 * Generated by: npx vibeship init
 * 
 * This component shows how to integrate Vibeship DevTools into your app
 */

'use client';

import { VibeshipProviderWithDeps } from '@vibeship/devtools';
import { VibeshipProvider } from '@vibeship/devtools';
import vibeshipConfig from '@/vibeship.config';

interface VibeshipSetupProps {
  children: React.ReactNode;
}

/**
 * Basic setup with all default dependencies
 * This is the simplest way to get started
 */
export function VibeshipSetup({ children }: VibeshipSetupProps) {
  return (
    <VibeshipProviderWithDeps config={vibeshipConfig}>
      {children}
    </VibeshipProviderWithDeps>
  );
}

/**
 * Advanced setup with custom dependencies
 * Use this if you need to customize the behavior
 */
export function VibeshipAdvancedSetup({ children }: VibeshipSetupProps) {
  // Custom task fetching
  const getProjectTasks = async (params: any) => {
    const response = await fetch(\`/api/vibeship/tasks?\${new URLSearchParams(params)}\`);
    const data = await response.json();
    return data.tasks;
  };
  
  // Custom SSE client
  const getSSEClient = (endpoint: string) => {
    const eventSource = new EventSource(endpoint);
    return {
      connect: () => {
        console.log('SSE Connected');
      },
      disconnect: () => {
        eventSource.close();
      },
      on: (event: string, handler: (data: any) => void) => {
        eventSource.addEventListener(event, (e: any) => {
          handler(JSON.parse(e.data));
        });
      },
      off: () => {
        // Remove listeners
      }
    };
  };
  
  // Custom API key loader (from localStorage or env)
  const onLoadApiKey = async (provider: string) => {
    // Example: Load from localStorage
    const key = localStorage.getItem(\`vibeship-api-key-\${provider}\`);
    
    // Or load from environment variables
    // const key = process.env[\`NEXT_PUBLIC_\${provider.toUpperCase()}_API_KEY\`];
    
    return key || '';
  };
  
  // Custom file operations
  const loadFileContent = async (filePath: string) => {
    const response = await fetch(\`/api/vibeship/files/content?path=\${encodeURIComponent(filePath)}\`);
    if (!response.ok) throw new Error('Failed to load file');
    const data = await response.json();
    return data.content;
  };
  
  const saveFileContent = async (filePath: string, content: string) => {
    const response = await fetch('/api/vibeship/files/content', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: filePath, content }),
    });
    if (!response.ok) throw new Error('Failed to save file');
  };
  
  return (
    <VibeshipProvider
      config={vibeshipConfig}
      getProjectTasks={getProjectTasks}
      sseEndpoint="/api/vibeship/tasks/stream"
      getSSEClient={getSSEClient}
      onLoadApiKey={onLoadApiKey}
      chatApiEndpoint="/api/vibeship/ai/chat"
      loadFileContent={loadFileContent}
      saveFileContent={saveFileContent}
    >
      {children}
    </VibeshipProvider>
  );
}

/**
 * Usage in your layout.tsx or _app.tsx:
 * 
 * import { VibeshipSetup } from '@/components/VibeshipSetup';
 * 
 * export default function RootLayout({ children }) {
 *   return (
 *     <html>
 *       <body>
 *         <VibeshipSetup>
 *           {children}
 *         </VibeshipSetup>
 *       </body>
 *     </html>
 *   );
 * }
 */
`;
}

export function getLayoutInjectionCode(isAppRouter: boolean): string {
  if (isAppRouter) {
    return `
        <VibeshipProviderWithDeps>
          {children}
        </VibeshipProviderWithDeps>`;
  } else {
    return `
      <VibeshipProviderWithDeps>
        <Component {...pageProps} />
      </VibeshipProviderWithDeps>`;
  }
}

export function getImportStatement(): string {
  return `import { VibeshipProviderWithDeps } from '@vibeship/devtools';`;
}